import { inject, Injectable, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { assert } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { EventTrackingService } from '../../../shared/event-tracking/event-tracking.service';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { PersistenceService } from '../../shared/misc-services/persistence.service';
import {
  annotationQueueName,
  AnnotationResource,
} from '../../shared/resources/annotation-resource.service';
import { PauseTaskDialogComponent } from '../task-paused-modal/pause-task-dialog.component';
import { TimerService } from '../timer-badge/timer.service';

interface PauseTaskEvent {
  method: 'auto_timeout' | 'pause_button';
  auto_time_out_duration?: number;
}

@Injectable()
export class TaskPausingService extends DirectiveSuperclass {
  private store = inject(TaskWorkspaceStore);
  private timerService = inject(TimerService);
  private eventTrackingService = inject(EventTrackingService);
  private matDialog = inject(MatDialog);
  private injector = inject(Injector);
  private annotationResource = inject(AnnotationResource);
  private persistenceService = inject(PersistenceService);

  pause(
    eventTrackingValue: PauseTaskEvent,
    pauseTimestamp = performance.now(),
  ) {
    if (this.timerService.status$.value === 'paused') {
      return;
    }

    this.eventTrackingService.track(
      {
        id: 'stop_duration_timer',
        bucket: 'untracked',
        ...eventTrackingValue,
      },
      { isInteractive: eventTrackingValue.method === 'pause_button' },
    );

    this.timerService.pause(pauseTimestamp);
    this.saveTaskDuration();
    this.openTaskPausedModal(eventTrackingValue);
  }

  resume(eventTrackingValue: PauseTaskEvent) {
    this.timerService.resume();

    this.eventTrackingService.track({
      id: 'resume_duration_timer',
      bucket: 'untracked',
      duration_since_last_active:
        performance.now() - this.getLastInteractiveEventTimestamp(),
      ...eventTrackingValue,
    });
  }

  private openTaskPausedModal(eventTrackingValue: PauseTaskEvent) {
    const ref = this.matDialog.open(PauseTaskDialogComponent, {
      width: '527px',
      height: '336px',
      backdropClass: 'dialog-backdrop',
      injector: this.injector,
    });
    this.subscribeTo(ref.afterClosed(), () => {
      this.resume(eventTrackingValue);
    });
  }

  private saveTaskDuration() {
    if (this.store.isUsingObjects()) {
      const duration = this.timerService.elapsed();
      this.persistenceService.enqueue$(
        this.annotationResource.updateMetadata$({ duration }),
        annotationQueueName,
      );
    }
  }

  private getLastInteractiveEventTimestamp() {
    const value = this.store.state().lastInteractiveEventTimestamp;
    assert(value, 'The lastInteractiveEventTimestamp must be defined');
    return value;
  }
}
